import { createDraftSafeSelector } from '@reduxjs/toolkit';

import type { RootState } from '@polyai/stream/store';

import { flowStepsSelectors } from '../flows';
import { isDefaultStep } from '../flows/types/steps';
import { flowsSelectors } from '../flows/utils';
import { entitySelectors } from '.';

export const selectEntitiesSlice = (state: RootState) => state.entities;

export const selectEntities = createDraftSafeSelector(
  [selectEntitiesSlice],
  ({ entities }) => {
    return entitySelectors.selectAll(entities);
  },
);

export const selectEntityByIds = createDraftSafeSelector(
  [selectEntitiesSlice, (_state: RootState, ids: string[]) => ids],
  ({ entities }, ids) => {
    return ids.map(id => {
      return entitySelectors.selectById(entities, id);
    });
  },
);

export const selectEntityById = createDraftSafeSelector(
  [selectEntitiesSlice, (_state: RootState, id: string) => id],
  ({ entities }, id) => {
    return entitySelectors.selectById(entities, id);
  },
);

export const selectEntityByName = createDraftSafeSelector(
  [selectEntitiesSlice, (_state: RootState, name: string) => name],
  ({ entities }, name) => {
    const allEntities = entitySelectors.selectAll(entities);
    return allEntities.find(entity => entity.name === name);
  },
);

export const selectEntitiesByStepRef = createDraftSafeSelector(
  [
    selectEntitiesSlice,
    (_state: RootState, flowId: string, stepId: string) => ({ flowId, stepId }),
  ],
  ({ entities }, { flowId, stepId }) => {
    const stepReferenceKey = `${flowId}/${stepId}`;
    const allEntities = entitySelectors.selectAll(entities);

    return allEntities.filter(
      entity =>
        entity.references?.flowSteps?.[stepReferenceKey] === true ||
        entity.references?.noCodeSteps?.[stepReferenceKey] === true,
    );
  },
);

export const selectExtractedEntities = createDraftSafeSelector(
  [
    selectEntitiesSlice,
    (state: RootState, flowId: string, stepId: string) => ({
      flow: flowsSelectors.selectById(state.flows.flows, flowId),
      stepId,
    }),
  ],
  ({ entities }, { flow, stepId }) => {
    const step = flow
      ? flowStepsSelectors.selectById(flow.steps, stepId)
      : undefined;
    const defaultStep = step && isDefaultStep(step) ? step : undefined;
    const extractedEntityIds = Object.keys(
      defaultStep?.references?.extractedEntities ?? {},
    );
    const allEntities = entitySelectors.selectAll(entities);
    const availableExtractedEntities = allEntities.filter(
      entity => !extractedEntityIds.includes(entity.id),
    );
    const extractedEntities = allEntities.filter(entity =>
      extractedEntityIds.includes(entity.id),
    );

    return {
      availableExtractedEntities,
      extractedEntities,
    };
  },
);
